<template>
  <resource-list-wrapper :title="$t('resources.phone_numbers.title')">
    <template #actions>
      <v-btn v-if="totalItems || noData || loading" color="primary" class="left-icon" to="/phone-numbers/create">
        <plus-icon class="mr-2"></plus-icon>
        {{ $t("generic.buttons.generate") }}
      </v-btn>
    </template>
    <template #default>
      <va-list
        ref="list"
        :resource="resourceName"
        :domains-list="domainsList"
        :filter.sync="customFiltersValues"
        :filters="filters"
        disable-export
        disable-create
        disable-clone
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template #[`filter.search`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table
          :fields="fields"
          class="va-table--selectable phone-number-table"
          actionsColWidth="64px"
          @row-click="handleRowClick"
          disable-select>
          <template #[`field.number`]="{ item }">
            <div class="d-flex">
              {{ item.number ? item.number : "-" }}
              <copy-text v-if="item.number" :value="item.number" display-icon />
            </div>
          </template>
          <template #[`field.campaignName`]="{ item }">
            {{ item.campaignName ? item.campaignName : "-" }}
          </template>
          <template #[`field.status`]="{ item }">
            <v-chip
              class="additional-label-chip capitalize"
              :class="{
                error: item.status === PHONE_NUMBER_STATUSES.ERROR,
                success: item.status === PHONE_NUMBER_STATUSES.ACTIVE,
              }">
              <span>
                {{ item.status === PHONE_NUMBER_STATUSES.ACTIVE ? "Generated" : item.status
                }}<span v-if="item.status === PHONE_NUMBER_STATUSES.GENERATING">...</span>
              </span>
            </v-chip>
          </template>
          <template #[`field.createdAt`]="{ item }">
            {{ changeDateFormat(item.createdAt, isUserUS($store.state.user.timezone)) }}
            {{ getHoursAndMinutes(item.createdAt) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'View',
                  icon: 'mdi-eye',
                  function: redirectAction.bind(this, { name: 'phone_numbers_show', params: { id: item.id } }),
                },
              ]">
            </DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import DataListActions from "@/components/custom/DataListActions";
  import debounce from "lodash.debounce";
  import definedAbility from "@/ability";
  import { PHONE_NUMBER_STATUSES } from "@/constant/index";
  import PlusIcon from "@/components/icons/PlusIcon";
  import CopyText from "@/components/custom/CopyText";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import RowClick from "@/mixins/rowClickMixin";
  import { changeDateFormat, getHoursAndMinutes } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";

  export default {
    components: {
      ResourceListWrapper,
      DataListActions,
      PlusIcon,
      CopyText,
    },
    mixins: [ConnectedDomainsList, RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        ability: definedAbility(),
        noData: false,
        loading: true,
        totalItems: 0,
        PHONE_NUMBER_STATUSES,
        customFiltersValues: this.activeFilterOnload,
        changeDateFormat,
        isUserUS,
        getHoursAndMinutes,
        filters: [
          {
            source: "search",
            alwaysOn: true,
          },
          {
            source: "status",
            label: "status",
            resource: "phone_numbers",
          },
          {
            source: "domain",
            label: this.$t("resources.phone_numbers.domain"),
          },
        ],
        fields: [
          {
            source: "number",
            label: this.$t("resources.phone_numbers.fields.number"),
            sortable: true,
            width: "20%",
          },
          {
            source: "publisherName",
            sortable: true,
            label: this.$t("resources.phone_numbers.fields.publisher_name"),
            width: "25%",
          },
          {
            source: "campaignName",
            sortable: true,
            label: this.$t("resources.phone_numbers.fields.campaign_name"),
            width: "25%",
          },
          {
            source: "status",
            label: this.$t("resources.phone_numbers.fields.status"),
            sortable: true,
            cellClass: "label-cell",
            width: "124px",
          },
          {
            source: "createdAt",
            label: this.$t("resources.phone_numbers.fields.created_at"),
            sortable: true,
            width: "152px",
          },
        ],
      };
    },
    methods: {
      redirectAction(resourceId) {
        this.$router.push(resourceId);
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .phone-number-table {
    ::v-deep {
      .v-data-table {
        .data-list--action-btn {
          min-width: unset !important;
        }

        .item-actions {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
        }

        .v-btn--icon {
          width: unset !important;
        }
      }
    }

    .slug-field {
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>
